// ── @matix/utils/storage ──────────────────────────────────────────────────
//
// Namespaced localStorage. Each frontend app passes its own namespace so
// keys stay isolated when running on the same parent domain (matix.one):
//
//   const store = createStorage('pos:cashier');
//   store.set('token', jwt);            // → 'pos:cashier:token'
//
// Patterns mirror warsha's ad-hoc localStorage usage but make the
// per-app prefix explicit and impossible to forget.

export interface Storage {
  get<T = unknown>(key: string): T | null;
  set<T>(key: string, value: T): void;
  remove(key: string): void;
  clear(): void; // clears only this namespace
}

export function createStorage(namespace: string): Storage {
  const prefix = `${namespace}:`;
  const isAvailable = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

  return {
    get<T = unknown>(key: string): T | null {
      if (!isAvailable) return null;
      try {
        const raw = window.localStorage.getItem(prefix + key);
        if (raw === null) return null;
        // Strings stored verbatim if they don't look like JSON
        if (raw.length === 0) return raw as unknown as T;
        const first = raw[0];
        if (first === '{' || first === '[' || first === '"' || raw === 'true' || raw === 'false' || raw === 'null' || /^-?\d/.test(raw)) {
          try {
            return JSON.parse(raw) as T;
          } catch {
            return raw as unknown as T;
          }
        }
        return raw as unknown as T;
      } catch {
        return null;
      }
    },

    set<T>(key: string, value: T): void {
      if (!isAvailable) return;
      try {
        const serialized = typeof value === 'string' ? value : JSON.stringify(value);
        window.localStorage.setItem(prefix + key, serialized);
      } catch {
        // quota / private mode / disabled — silently ignore
      }
    },

    remove(key: string): void {
      if (!isAvailable) return;
      try {
        window.localStorage.removeItem(prefix + key);
      } catch {
        /* noop */
      }
    },

    clear(): void {
      if (!isAvailable) return;
      try {
        const toRemove: string[] = [];
        for (let i = 0; i < window.localStorage.length; i++) {
          const k = window.localStorage.key(i);
          if (k && k.startsWith(prefix)) toRemove.push(k);
        }
        for (const k of toRemove) window.localStorage.removeItem(k);
      } catch {
        /* noop */
      }
    },
  };
}
